import {
  Box,
  Button,
  Card,
  Fade,
  Stack,
  Tab,
  Tabs,
  Tooltip,
} from '@mui/material';
import {
  BulkAction,
  FeeStatus,
  InstallmentStatus,
  PaymentStatus,
  ReceiptType,
  type StudentFeeFilter,
  usePermissions,
} from '@tyro/api';
import {
  ActionMenu,
  type AgGridReact,
  type BasicListNavigatorMenuItemParams,
  ConfirmDialog,
  type GetDetailRowDataParams,
  type GridOptions,
  type ICellRendererParams,
  ListNavigatorType,
  type ReturnTypeDisplayName,
  Table,
  TablePersonAvatar,
  commonActionMenuProps,
  getNumber,
  toast,
  useDisclosure,
  useListNavigatorSettings,
  usePreferredNameLayout,
} from '@tyro/core';
import { type TFunction, useFormatNumber, useTranslation } from '@tyro/i18n';
import {
  DiscountIcon,
  EyeIcon,
  InfoCircleIcon,
  ReceiptIcon,
  RemoveDiscountIcon,
  VerticalDotsIcon,
  WalletCheckmarkIcon,
  WalletWithMoneyIcon,
} from '@tyro/icons';
import { useNavigate, useSearchParams } from '@tyro/router/hooks';
import dayjs from 'dayjs';
import LocalizedFormat from 'dayjs/plugin/localizedFormat';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useBulkApplyDiscounts } from '../../api/bulk-apply-discounts';
import {
  type ReturnTypeFromUsePayments,
  usePayments,
} from '../../api/payments';
import { getPrintFees } from '../../api/print-fees';
import {
  type ReturnTypeFromUseStudentFees,
  useStudentFees,
} from '../../api/student-fees';
import { getDiscountName } from '../../utils/get-discount-name';
import {
  BulkAddIndividualDiscountModal,
  type BulkAddIndividualDiscountModalProps,
  DISCOUNT_ID_FOR_ADHOC,
} from '../fees/form/bulk-add-individual-discount-modal';
import { getInstallmentIntervalValueFromFormData } from '../fees/form/installment-interval-field';
import { WriteOffBalanceMultipleFeesModal } from '../fees/write-off-balance-multiple-fees-modal';
import { FeeStatusChip } from './fee-status-chip';
import { InstallmentStatusChip } from './installment-status-chip';
import { PayFeesModal } from './pay-fees-modal';

dayjs.extend(LocalizedFormat);

interface StudentFeeTableProps {
  filter: StudentFeeFilter;
}

const getColumnDefs = (
  t: TFunction<('fees' | 'common')[], undefined, ('fees' | 'common')[]>,
  onBeforeNavigate: () => void,
  formatCurrency: ReturnType<typeof useFormatNumber>['formatCurrency'],
  isStaffUser: boolean,
  payments: ReturnTypeFromUsePayments[],
  onPayClick: (feeId: ReturnTypeFromUseStudentFees['id']) => void,
  onPrintReceipt: (fee: ReturnTypeFromUseStudentFees) => void,
  displayName: ReturnTypeDisplayName,
  isScheduledTab: boolean,
): GridOptions<ReturnTypeFromUseStudentFees>['columnDefs'] => {
  const scheduledColumns: GridOptions<ReturnTypeFromUseStudentFees>['columnDefs'] =
    [
      {
        colId: 'installmentInterval',
        headerName: t('fees:planInterval'),
        hide: !isScheduledTab,
        valueGetter: ({ data }) => {
          if (!data) return '-';
          const paymentPlanInterval =
            getInstallmentIntervalValueFromFormData(data);
          return t(`fees:installmentIntervalType.${paymentPlanInterval}`);
        },
      },
      {
        colId: 'installments',
        headerName: t('fees:installments'),
        hide: !isScheduledTab,
        valueGetter: ({ data }) =>
          data?.activePaymentPlan?.installments.length ?? 0,
      },
      {
        colId: 'nextPayment',
        headerName: t('fees:nextPayment'),
        hide: !isScheduledTab,
        valueFormatter: ({ data }) => {
          const installments = data?.activePaymentPlan?.installments || [];
          if (!installments.length) return '-';

          const nextPayment = installments.find(
            ({ status }) => status === InstallmentStatus.Pending,
          );

          return nextPayment
            ? dayjs(nextPayment?.installmentDate).format('LL')
            : '-';
        },
      },
      {
        field: 'activePaymentPlan.cardUsed',
        headerName: t('fees:cardUsed'),
        valueGetter: ({ data }) => {
          const cardUsed = data?.activePaymentPlan?.cardUsed;
          if (!cardUsed) return '-';
          return `*****${cardUsed}`;
        },
      },
      {
        field: 'activePaymentPlan.payee',
        headerName: t('fees:payeeName'),
        valueGetter: ({ data }) =>
          data?.activePaymentPlan?.payee
            ? displayName(data?.activePaymentPlan?.payee)
            : '-',
      },
    ];

  return [
    {
      field: 'person',
      headerName: t('fees:for'),
      lockVisible: true,
      valueGetter: ({ data }) => displayName(data?.person),
      cellRenderer: isScheduledTab
        ? 'agGroupCellRenderer'
        : ({ data }: ICellRendererParams<ReturnTypeFromUseStudentFees>) => {
            if (!data) return null;
            const { person } = data;

            return <TablePersonAvatar person={person} />;
          },
    },
    {
      field: 'feeName',
      headerName: t('fees:feeName'),
    },
    ...(isScheduledTab ? scheduledColumns : []),
    {
      field: 'dueDate',
      headerName: t('fees:dueBy'),
      valueFormatter: ({ data }) =>
        data?.dueDate ? dayjs(data.dueDate).format('LL') : '-',
      sort: 'asc',
    },
    {
      field: 'amount',
      headerName: t('fees:amount'),
      cellClass: ['currencyFormat', 'ag-right-aligned-cell'],
      valueFormatter: ({ data }) => formatCurrency(data?.amount ?? 0),
      aggFunc: 'sum',
      comparator: (a: number, b: number) => a - b,
      type: 'numericColumn',
    },
    {
      field: 'amountDiscounted',
      headerName: t('fees:discounted'),
      cellClass: ['currencyFormat', 'ag-right-aligned-cell'],
      valueFormatter: ({ data }) => formatCurrency(data?.amountDiscounted ?? 0),
      aggFunc: 'sum',
      comparator: (a: number, b: number) => a - b,
      type: 'numericColumn',
    },
    {
      field: 'amountWrittenOff',
      headerName: t('fees:amountWrittenOff'),
      cellClass: ['currencyFormat', 'ag-right-aligned-cell'],
      valueFormatter: ({ data }) => formatCurrency(data?.amountWrittenOff ?? 0),
      aggFunc: 'sum',
      comparator: (a: number, b: number) => a - b,
      type: 'numericColumn',
    },
    {
      field: 'amountDue',
      headerName: t('fees:due'),
      cellClass: ['currencyFormat', 'ag-right-aligned-cell'],
      valueFormatter: ({ data }) => formatCurrency(data?.amountDue ?? 0),
      aggFunc: 'sum',
      comparator: (a: number, b: number) => a - b,
      type: 'numericColumn',
    },
    {
      field: 'amountPaid',
      headerName: t('fees:amountPaid'),
      cellClass: ['currencyFormat', 'ag-right-aligned-cell'],
      valueFormatter: ({ data }) => formatCurrency(data?.amountPaid ?? 0),
      aggFunc: 'sum',
      comparator: (a: number, b: number) => a - b,
      type: 'numericColumn',
    },
    {
      field: 'discounts',
      headerName: t('fees:discounts'),
      hide: !isStaffUser,
      suppressColumnsToolPanel: !isStaffUser,
      valueGetter: ({ data }) => {
        const [discount] = data?.discounts || [];
        if (!discount) return '-';

        return getDiscountName({
          ...discount,
          value:
            discount.id === DISCOUNT_ID_FOR_ADHOC
              ? (data?.adHocAmount ?? 0)
              : discount.value,
        });
      },
    },
    {
      field: 'feeStatus',
      headerName: t('common:status'),
      valueGetter: ({ data }) =>
        data?.feeStatus ? t(`fees:status.${data.feeStatus}`) : '-',
      cellRenderer: ({
        data,
      }: ICellRendererParams<ReturnTypeFromUseStudentFees>) => {
        if (!data?.feeStatus) return '-';

        return (
          <Tooltip title={data?.writeOffNote} placement="top" arrow>
            <Stack gap={0.5} flexDirection="row">
              <FeeStatusChip status={data?.feeStatus} />
              {data?.writeOffNote && (
                <InfoCircleIcon sx={{ width: 18, height: 18 }} />
              )}
            </Stack>
          </Tooltip>
        );
      },
      sort: 'asc',
      sortIndex: 0,
      enableRowGroup: true,
    },
    {
      ...commonActionMenuProps,
      hide: !isStaffUser,
      cellRenderer: ({
        data,
      }: ICellRendererParams<ReturnTypeFromUseStudentFees>) => {
        if (!data) return null;
        const isPaid = data.feeStatus === FeeStatus.Complete;
        const hasTxs = payments.some(({ fee }) => fee.id === data.id.feeId);
        const isRecurringPayment =
          data.paymentPlan &&
          data.activePaymentPlan?.installments?.[0]?.status ===
            InstallmentStatus.Paid;

        const disabledPaidForPaymentPlan =
          isRecurringPayment &&
          t('fees:cantPayAsSelectedFeeIsPartOfRecurringPaymentPlan');

        const disabledPaidForFullyPaid =
          isPaid && t('fees:cantPayAsSelectedFeeHaveBeenFullyPaid');

        const { feeId, studentPartyId } = data.id;

        return (
          <ActionMenu
            iconOnly
            buttonIcon={<VerticalDotsIcon />}
            menuItems={[
              {
                label: t('fees:pay'),
                icon: <WalletWithMoneyIcon />,
                disabled: isPaid || isRecurringPayment,
                disabledTooltip:
                  disabledPaidForFullyPaid ||
                  disabledPaidForPaymentPlan ||
                  undefined,
                onClick: () => onPayClick(data.id),
              },
              {
                label: t('fees:statement'),
                icon: <ReceiptIcon />,
                disabled: !hasTxs,
                disabledTooltip: hasTxs
                  ? undefined
                  : t('fees:youCantPrintStatement'),
                onClick: () => onPrintReceipt(data),
              },
              {
                label: t('fees:viewTransactions'),
                icon: <EyeIcon />,
                disabled: !hasTxs,
                disabledTooltip: hasTxs
                  ? undefined
                  : t('fees:cantViewTransactionFee'),
                navigateTo: `/fees/transactions/${feeId}?studentPartyId=${studentPartyId}`,
                onBeforeNavigate,
                hasAccess: ({ isStaffUserWithPermission }) =>
                  isStaffUserWithPermission('ps:1:fees:write_fees'),
              },
            ]}
          />
        );
      },
    },
  ];
};

const installmentColDefs = (
  t: TFunction<('common' | 'fees')[], undefined, ('common' | 'fees')[]>,
  formatCurrency: ReturnType<typeof useFormatNumber>['formatCurrency'],
): GridOptions<
  NonNullable<
    ReturnTypeFromUseStudentFees['activePaymentPlan']
  >['installments'][number]
>['columnDefs'] => [
  {
    field: 'installment',
    headerName: t('fees:installment'),
    valueFormatter: ({ data }) =>
      data?.installment ? `#${data?.installment}` : '',
  },
  {
    field: 'installmentDate',
    headerName: t('fees:dueBy'),
    valueFormatter: ({ value }) => (value ? dayjs(value).format('LL') : '-'),
  },
  {
    field: 'amount',
    headerName: t('fees:amount'),
    cellClass: ['currencyFormat', 'ag-right-aligned-cell'],
    valueFormatter: ({ data }) => formatCurrency(data?.amount ?? 0),
    type: 'numericColumn',
  },
  {
    field: 'serviceCharges',
    headerName: t('fees:serviceCharges'),
    cellClass: ['currencyFormat', 'ag-right-aligned-cell'],
    valueFormatter: ({ data }) => formatCurrency(data?.serviceCharges ?? 0),
    type: 'numericColumn',
  },
  {
    field: 'status',
    headerName: t('common:status'),
    valueGetter: ({ data }) =>
      data?.status ? t(`fees:installmentStatus.${data.status}`) : '',
    cellRenderer: ({
      data,
    }: ICellRendererParams<
      NonNullable<
        ReturnTypeFromUseStudentFees['activePaymentPlan']
      >['installments'][number]
    >) => (data?.status ? <InstallmentStatusChip status={data.status} /> : ''),
  },
];

enum TabFilter {
  Due = 'DUE',
  Scheduled = 'SCHEDULED',
  Paid = 'PAID',
}

export function StudentFeesTable({ filter }: StudentFeeTableProps) {
  const { t } = useTranslation(['common', 'fees']);
  const { formatCurrency } = useFormatNumber();
  const { displayName } = usePreferredNameLayout();

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const params = {
    tab: searchParams.get('tab') ?? '',
    feeId: getNumber(searchParams.get('feeId')),
    studentPartyId: getNumber(searchParams.get('studentPartyId')) ?? 0,
  };

  const tableRef = useRef<AgGridReact<ReturnTypeFromUseStudentFees>>(null);

  const [selectedStudentFeeIds, setSelectedStudentFeeIds] = useState<
    Set<string>
  >(new Set());

  const { isStaffUser, isStaffUserWithPermission } = usePermissions();
  const { mutateAsync: bulkApplyDiscounts } = useBulkApplyDiscounts();

  const {
    isOpen: isPayFeesModalOpen,
    onOpen: onOpenPayFeesModal,
    onClose: onClosePayFeesModal,
  } = useDisclosure();

  const {
    isOpen: isAddBulkDiscountModalOpen,
    onOpen: onOpenAddBulkDiscountModal,
    onClose: onCloseAddBulkDiscountModal,
  } = useDisclosure();

  const {
    isOpen: isRemoveDiscountConfirmOpen,
    onOpen: onOpenRemoveDiscountConfirm,
    onClose: onCloseRemoveDiscountConfirm,
  } = useDisclosure();

  const {
    isOpen: isWriteOffBalanceModalOpen,
    onOpen: onOpenWriteOffBalanceModal,
    onClose: onCloseWriteOffBalanceModal,
  } = useDisclosure();

  const { data: studentFees = [] } = useStudentFees(filter);

  const { data: paymentsData = [] } = usePayments({
    studentPartyIds: studentFees.map(
      (studentFee) => studentFee.id.studentPartyId,
    ),
    status: PaymentStatus.Succeeded,
  });

  const selectedStudentFees = useMemo(
    () =>
      studentFees?.filter((studentFee) =>
        selectedStudentFeeIds.has(JSON.stringify(studentFee.id)),
      ) ?? [],
    [studentFees, selectedStudentFeeIds],
  );

  const handleIndividualPayment = useCallback(
    (studentFeeId: ReturnTypeFromUseStudentFees['id']) => {
      setSelectedStudentFeeIds(new Set([JSON.stringify(studentFeeId)]));
      onOpenPayFeesModal();
    },
    [],
  );

  const { storeList } =
    useListNavigatorSettings<BasicListNavigatorMenuItemParams>({
      type: ListNavigatorType.StudentFees,
    });

  const visibleDataRef = useRef<() => ReturnTypeFromUseStudentFees[]>(null);
  const [currentTabFilter, setCurrentTabFilter] = useState(TabFilter.Due);

  const onBeforeNavigateProfile = useCallback(() => {
    storeList(
      t('fees:feesPage'),
      visibleDataRef.current?.().map(({ id, feeName }) => ({
        id: id.feeId,
        name: feeName,
      })),
    );
  }, []);

  const handlePrintReceipt = useCallback(
    async (fee: ReturnTypeFromUseStudentFees) => {
      try {
        const printResponse = await getPrintFees({
          feeId: fee.id.feeId,
          type: ReceiptType.Statement,
          studentId: fee.id.studentPartyId,
        });

        if (printResponse?.print_fees?.url)
          window.open(printResponse.print_fees.url, '_blank', 'noreferrer');
      } catch {
        toast.error(t('common:snackbarMessages.errorFailed'));
      }
    },
    [],
  );

  const columnDefs = useMemo(
    () =>
      getColumnDefs(
        t,
        onBeforeNavigateProfile,
        formatCurrency,
        isStaffUser,
        paymentsData,
        handleIndividualPayment,
        handlePrintReceipt,
        displayName,
        currentTabFilter === TabFilter.Scheduled,
      ),
    [
      t,
      onBeforeNavigateProfile,
      formatCurrency,
      isStaffUser,
      paymentsData,
      handleIndividualPayment,
      handlePrintReceipt,
      displayName,
      currentTabFilter,
    ],
  );

  const handleSaveIndividualDiscount: BulkAddIndividualDiscountModalProps['onSave'] =
    ({ discount, adhocAmount }) => {
      bulkApplyDiscounts(
        selectedStudentFees.map((fee) => ({
          ...fee.id,
          action: BulkAction.Upsert,
          discountId: discount.id,
          adhocAmount,
        })),
        {
          onSuccess: onCloseAddBulkDiscountModal,
        },
      );
    };

  const removeDiscounts = () =>
    bulkApplyDiscounts(
      selectedStudentFees
        .filter(({ discounts }) => discounts.length > 0)
        .map((fee) => ({
          ...fee.id,
          action: BulkAction.Remove,
          discountId: fee.discounts[0].id,
        })),
      {
        onSuccess: onCloseRemoveDiscountConfirm,
      },
    );

  const someFeesHaveBeenPaid = selectedStudentFees.some(
    ({ feeStatus }) => feeStatus === FeeStatus.Complete,
  );

  const doSomeSelectedDebtorsHaveDiscounts = selectedStudentFees.some(
    ({ discounts }) => discounts.length > 0,
  );

  const haveSomeSelectedDebtorsPaidSomething = selectedStudentFees.some(
    ({ amountPaid }) => amountPaid > 0,
  );

  const minimumAmount = useMemo(() => {
    if (selectedStudentFees.length === 0) return 0;

    return Math.min(
      ...selectedStudentFees.map(
        ({ amountDue, amountDiscounted }) => amountDue + amountDiscounted,
      ),
    );
  }, [selectedStudentFees]);

  const useHasWriteFeesPermission = isStaffUserWithPermission(
    'ps:1:fees:write_fees',
  );

  const filteredStudentFees = useMemo(() => {
    switch (currentTabFilter) {
      case TabFilter.Due: {
        return studentFees.filter(
          ({ feeStatus, paymentPlan, activePaymentPlan }) => {
            const isNotPaid = feeStatus !== FeeStatus.Complete;
            const hasNoPaymentPlan = !paymentPlan;
            const hasPendingPaymentPlan = paymentPlan && !activePaymentPlan;

            return isNotPaid && (hasNoPaymentPlan || hasPendingPaymentPlan);
          },
        );
      }
      case TabFilter.Scheduled: {
        return studentFees.filter(
          ({ paymentPlan, activePaymentPlan }) =>
            paymentPlan && activePaymentPlan,
        );
      }
      case TabFilter.Paid: {
        return studentFees.filter(
          ({ feeStatus, amountPaid }) =>
            feeStatus === FeeStatus.Complete || amountPaid > 0,
        );
      }
    }
  }, [currentTabFilter, studentFees]);

  const detailCellRendererParams = useMemo(
    () => ({
      detailGridOptions: {
        columnDefs: installmentColDefs(t, formatCurrency),
      },
      getDetailRowData: (
        params: GetDetailRowDataParams<ReturnTypeFromUseStudentFees>,
      ) => {
        params.successCallback(
          params.data.activePaymentPlan?.installments || [],
        );
      },
    }),
    [t, formatCurrency],
  );

  useEffect(() => {
    const { tab } = params;

    if (tab) {
      const hasNotifiedFeeBeenPaid =
        studentFees.find(
          (fee) =>
            fee.id.feeId === params.feeId &&
            fee.id.studentPartyId === params.studentPartyId,
        )?.feeStatus === FeeStatus.Complete;

      if (hasNotifiedFeeBeenPaid) {
        setCurrentTabFilter(TabFilter.Paid);
      } else {
        setCurrentTabFilter(tab as TabFilter);
      }

      setTimeout(() => {
        tableRef?.current?.api?.forEachNode((node) => {
          if (
            node.data?.id.feeId === params.feeId &&
            node.data?.id.studentPartyId === params.studentPartyId
          ) {
            node.setExpanded(true);
            node.setSelected(true);
            tableRef.current?.api?.ensureNodeVisible(node, 'top');
          }
        });
        navigate('/fees/list', { replace: true });
      }, 500);
    }
  }, [JSON.stringify(params)]);

  return (
    <>
      <Card
        variant="soft"
        sx={{ display: 'flex', flex: 1, flexDirection: 'column' }}
      >
        <Tabs
          value={currentTabFilter}
          onChange={(_event, newValue: TabFilter) => {
            setSelectedStudentFeeIds(new Set());
            setCurrentTabFilter(newValue);
          }}
          sx={{
            '& .MuiTabs-flexContainer': {
              alignItems: 'center',
              marginLeft: 2,
            },
            '& .MuiTabs-flexContainer > .MuiButtonBase-root': {
              marginRight: 3.5,
            },
          }}
        >
          {Object.values(TabFilter).map((value) => (
            <Tab
              key={value}
              value={value}
              label={t(`fees:feesFilterTypes.${value}`)}
            />
          ))}
        </Tabs>
        <Table
          ref={tableRef}
          key={currentTabFilter}
          visibleDataRef={visibleDataRef}
          rowData={filteredStudentFees || []}
          columnDefs={columnDefs}
          getRowId={({ data }) => JSON.stringify(data?.id)}
          onRowSelection={(selectedRows) => {
            setSelectedStudentFeeIds(
              new Set(selectedRows.map((row) => JSON.stringify(row.id))),
            );
          }}
          getRowClass={(row) => {
            const isHighlighted =
              row?.data?.id.feeId === params.feeId &&
              row?.data?.id.studentPartyId === params.studentPartyId;
            return isHighlighted ? 'ag-row-hover' : '';
          }}
          {...(currentTabFilter === TabFilter.Scheduled && {
            masterDetail: true,
            detailCellRendererParams,
            tableContainerSx: {
              '& .ag-details-row': {
                padding: 'var(--ag-grid-size)',
              },
            },
          })}
          {...(currentTabFilter === TabFilter.Due && {
            rowSelection: 'multiple',
            rightAdornment: (
              <Fade in={selectedStudentFees.length > 0} unmountOnExit>
                {useHasWriteFeesPermission ? (
                  <Box>
                    <ActionMenu
                      menuItems={[
                        {
                          label: t('fees:pay'),
                          icon: <WalletWithMoneyIcon />,
                          disabled: someFeesHaveBeenPaid,
                          disabledTooltip: someFeesHaveBeenPaid
                            ? t(
                                'fees:cantPayAsSomeSelectedFeesHaveBeenFullyPaid',
                              )
                            : undefined,
                          onClick: onOpenPayFeesModal,
                        },
                        {
                          label: t('fees:writeOffBalance'),
                          icon: <WalletCheckmarkIcon />,
                          disabled: someFeesHaveBeenPaid,
                          disabledTooltip: someFeesHaveBeenPaid
                            ? t(
                                'fees:youCanNotWriteOffBalanceAsSomeFeesHaveHaveBeenFullyPaid',
                              )
                            : undefined,
                          onClick: onOpenWriteOffBalanceModal,
                        },
                        {
                          label: doSomeSelectedDebtorsHaveDiscounts
                            ? t('fees:replaceDiscount')
                            : t('fees:addDiscount'),
                          icon: <DiscountIcon />,
                          disabled: haveSomeSelectedDebtorsPaidSomething,
                          disabledTooltip: haveSomeSelectedDebtorsPaidSomething
                            ? t(
                                `fees:${
                                  doSomeSelectedDebtorsHaveDiscounts
                                    ? 'youCanNotReplaceDiscountAsSomePeopleHaveAlreadyPaidFee'
                                    : 'youCanNotAddDiscountAsSomePeopleHaveAlreadyPaidFee'
                                }`,
                              )
                            : undefined,
                          onClick: onOpenAddBulkDiscountModal,
                        },
                        {
                          label: t('fees:removeDiscount', {
                            count: selectedStudentFees.length,
                          }),
                          icon: <RemoveDiscountIcon />,
                          hasAccess: () => doSomeSelectedDebtorsHaveDiscounts,
                          disabled: haveSomeSelectedDebtorsPaidSomething,
                          disabledTooltip: haveSomeSelectedDebtorsPaidSomething
                            ? t(
                                'fees:youCanNotRemoveDiscountAsSomePeopleHaveAlreadyPaidFee',
                                { count: selectedStudentFees.length },
                              )
                            : undefined,
                          onClick: onOpenRemoveDiscountConfirm,
                        },
                      ]}
                    />
                  </Box>
                ) : (
                  <Tooltip
                    title={
                      someFeesHaveBeenPaid
                        ? t('fees:cantPayAsSomeSelectedFeesHaveBeenFullyPaid')
                        : undefined
                    }
                  >
                    <Box>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={onOpenPayFeesModal}
                        disabled={someFeesHaveBeenPaid}
                      >
                        {t('fees:pay')}
                      </Button>
                    </Box>
                  </Tooltip>
                )}
              </Fade>
            ),
          })}
        />
      </Card>

      <PayFeesModal
        open={isPayFeesModalOpen}
        onClose={onClosePayFeesModal}
        feesToPay={selectedStudentFees}
      />

      <WriteOffBalanceMultipleFeesModal
        isOpen={isWriteOffBalanceModalOpen}
        feesToWriteOff={selectedStudentFees}
        onClose={onCloseWriteOffBalanceModal}
      />

      <BulkAddIndividualDiscountModal
        isOpen={isAddBulkDiscountModalOpen}
        feeAmount={minimumAmount}
        onSave={handleSaveIndividualDiscount}
        onClose={onCloseAddBulkDiscountModal}
      />

      <ConfirmDialog
        open={isRemoveDiscountConfirmOpen}
        title={t('fees:removeDiscount', { count: selectedStudentFees.length })}
        description={t('fees:areYouSureYouWantToRemoveDiscount', {
          count: selectedStudentFees.length,
        })}
        confirmText={t('common:yes')}
        cancelText={t('common:no')}
        onConfirm={removeDiscounts}
        onClose={onCloseRemoveDiscountConfirm}
      />
    </>
  );
}
